import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogIn, LogOut } from 'lucide-react';
import { siteConfig, navLinks } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    // Check login status
    const token = localStorage.getItem('adminToken');
    setIsLoggedIn(!!token);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    setIsLoggedIn(false);
    navigate('/');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled 
          ? 'bg-black py-4' 
          : 'bg-transparent py-6 lg:py-8'
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link 
          to="/" 
          className="group relative z-10"
        >
          <span className="font-serif text-lg md:text-xl tracking-[0.25em] text-white uppercase font-normal">
            Mariam Sallam
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-10">
          <ul className="flex items-center gap-10">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`relative font-sans text-[11px] tracking-[0.2em] uppercase transition-all duration-300 ${
                    isActive(link.path)
                      ? 'text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  {link.label}
                  <span 
                    className={`absolute -bottom-1 left-0 h-[1px] bg-white transition-all duration-300 ${
                      isActive(link.path) ? 'w-full' : 'w-0'
                    }`} 
                  />
                </Link>
              </li>
            ))}
          </ul>
          
          {/* Login/Logout Button */}
          {isLoggedIn ? (
            <div className="flex items-center gap-4 ml-6 pl-6 border-l border-white/20">
              <Link
                to="/admin"
                className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 font-sans text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors"
              >
                <LogOut size={14} />
                Logout
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="flex items-center gap-2 ml-6 pl-6 border-l border-white/20 font-sans text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors"
            >
              <LogIn size={14} />
              Login
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden text-white p-2 z-10"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 bg-black transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <ul className="flex flex-col items-center justify-center h-full gap-8">
          {navLinks.map((link, index) => (
            <li 
              key={link.path}
              style={{ 
                transitionDelay: isMobileMenuOpen ? `${index * 80}ms` : '0ms' 
              }}
              className={`transform transition-all duration-500 ${
                isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
            >
              <Link
                to={link.path}
                className={`font-serif text-2xl md:text-3xl tracking-wider transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-white'
                    : 'text-white/50 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            </li>
          ))}
          
          {/* Mobile Login/Logout */}
          <li 
            style={{ transitionDelay: isMobileMenuOpen ? `${navLinks.length * 80}ms` : '0ms' }}
            className={`transform transition-all duration-500 mt-8 pt-8 border-t border-white/20 ${
              isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            {isLoggedIn ? (
              <div className="flex flex-col items-center gap-4">
                <Link
                  to="/admin"
                  className="font-serif text-xl tracking-wider text-white/50 hover:text-white transition-colors"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 font-serif text-xl tracking-wider text-white/50 hover:text-white transition-colors"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 font-serif text-xl tracking-wider text-white/50 hover:text-white transition-colors"
              >
                <LogIn size={18} />
                Login
              </Link>
            )}
          </li>
        </ul>
      </div>
    </header>
  );
};

export default Header;
